import { Link2, X } from 'lucide-react'
import { createContext, use, type ReactNode } from 'react'

import { type OrigenDeVinculos, useVinculables, type Vinculables } from '../api/estructura'
import { Boton, Pastilla } from '../componentes/ui'
import { aplanar, type Campo } from '../motor/esquema'
import { useFormulario } from '../motor/Formulario'
import { leer } from '../motor/rutas'
import {
  agrupar,
  CLASES_DE_TEXTO,
  claseDelCampo,
  conVinculo,
  destinosSinCampo,
  etiquetaDelVinculo,
  expresionDe,
  nombreDelDestino,
  queCabenEn,
  rutaEnElBloque,
  type Vinculo,
} from './reglasDeVinculos'

/**
 * «Vincular a…» en el editor de plantillas (plan del CMS dinámico §7). Una plantilla es un
 * campo de bloques del catálogo de siempre; cada campo de un bloque puede, en vez de un valor
 * escrito, tomar un dato del documento (`hero.titulo ← @titulo`). Lo vinculado se guarda en el
 * mapa `vinculos` del bloque, que no se pinta como JSON: cada campo enseña su selector.
 *
 * Tres piezas, de fuera adentro:
 * - `PlantillasConVinculos` (la pantalla del tipo o de la taxonomía): qué campo de bloques es
 *   qué plantilla, y de qué documento salen sus datos.
 * - `BloqueDePlantilla` (lo pone el motor alrededor de cada bloque de una plantilla): la ruta
 *   del bloque y lo que el núcleo dice que se puede vincular.
 * - `CampoVinculable` (lo pone el motor alrededor de cada control de ese bloque).
 *
 * Qué expresiones hay y dónde caben lo dice el núcleo (`/panel/estructura/vinculables`); la
 * comprobación de verdad es la suya, al guardar, y sus errores salen en el bloque.
 */

type ContextoDePlantillas = {
  /** De la ruta de un campo de bloques (`plantillaFicha`), de dónde salen sus datos; `null` si no es una plantilla. */
  origenDe: (rutaDelCampo: string) => null | OrigenDeVinculos
  /** El tipo o la taxonomía todavía no se ha guardado: no hay a qué vincular. */
  sinGuardar: boolean
}

const Plantillas = createContext<ContextoDePlantillas | null>(null)

export const PlantillasConVinculos = ({ children, ...valor }: ContextoDePlantillas & { children: ReactNode }) => <Plantillas value={valor}>{children}</Plantillas>

type ContextoDelBloque = {
  rutaDelBloque: string
  bloque: string
  datos: undefined | Vinculables
  sinGuardar: boolean
}

const BloqueActual = createContext<ContextoDelBloque | null>(null)

export const useBloqueDePlantilla = () => use(BloqueActual)

/** Si un bloque lleva el mapa de vínculos (el núcleo lo marca con `control: vinculos`). */
export const esBloqueDePlantilla = (campos: Campo[]): boolean => aplanar(campos).some((c) => c.control?.control === 'vinculos')

/** El control del mapa `vinculos` no se pinta: lo escriben los selectores de cada campo. */
export const esMapaDeVinculos = (campo: Campo): boolean => campo.control?.control === 'vinculos'

/** Lo que el motor necesita de un campo de bloques: si es una plantilla, y lo que se puede vincular en ella (una petición por plantilla). */
export const usePlantilla = (rutaDelCampo: string) => {
  const contexto = use(Plantillas)
  const origen = contexto && !contexto.sinGuardar ? contexto.origenDe(rutaDelCampo) : null
  const esPlantilla = Boolean(contexto && (contexto.sinGuardar || origen))
  const { data, isError } = useVinculables(origen)
  return esPlantilla ? { datos: data, fallo: isError, sinGuardar: Boolean(contexto?.sinGuardar) } : null
}

/** Alrededor de los campos de un bloque de plantilla: su contexto, los errores de sus vínculos y los destinos que no son campos. */
export const BloqueDePlantilla = ({
  children,
  plantilla,
  rutaDelBloque,
  bloque,
  campos,
}: {
  bloque: string
  campos: Campo[]
  children: ReactNode
  plantilla: { datos: undefined | Vinculables; fallo: boolean; sinGuardar: boolean }
  rutaDelBloque: string
}) => {
  const { errores } = useFormulario()
  const prefijo = `${rutaDelBloque}.vinculos.`
  const problemas = Object.entries(errores).filter(([ruta]) => ruta === `${rutaDelBloque}.vinculos` || ruta.startsWith(prefijo))
  const nombres = aplanar(campos)
    .map((c) => c.nombre)
    .filter((n): n is string => Boolean(n))
  const destinos = destinosSinCampo(plantilla.datos?.bloques, bloque, nombres)
  return (
    <BloqueActual value={{ rutaDelBloque, bloque, datos: plantilla.datos, sinGuardar: plantilla.sinGuardar }}>
      {plantilla.sinGuardar && <p className="text-xs text-tenue">Guarda primero para poder vincular los campos de este bloque a los datos de cada documento.</p>}
      {plantilla.fallo && <p className="text-xs text-tenue">No se han podido leer los datos que se pueden vincular. Recarga la página para volver a intentarlo.</p>}
      {problemas.length > 0 && (
        <ul className="flex flex-col gap-1 rounded-caja border border-error/30 bg-error-suave px-3 py-2 text-[13px]" role="alert">
          {problemas.map(([ruta, mensaje]) => (
            <li key={ruta}>{mensaje}</li>
          ))}
        </ul>
      )}
      {children}
      {destinos.map((destino) => (
        <CampoVinculable campo={{ tipo: 'json', etiqueta: nombreDelDestino(destino), nombre: destino }} key={destino} ruta={`${rutaDelBloque}.${destino}`} sinControl>
          {null}
        </CampoVinculable>
      ))}
    </BloqueActual>
  )
}

/** Los números que dicen cómo se ve un bloque, no qué enseña. */
const NUMEROS_DE_MAQUETACION = new Set(['columnas', 'cuantos', 'cuantas', 'porPagina', 'limite'])

/** Un campo de maquetación (una opción, una casilla, o un número de los de arriba): no se ofrece «Vincular a…». */
export const esDeMaquetacion = (campo: Pick<Campo, 'nombre' | 'tipo'>, clase: null | string | undefined): boolean =>
  clase === 'select' || clase === 'checkbox' || (clase === 'number' && NUMEROS_DE_MAQUETACION.has(campo.nombre ?? ''))

/**
 * Un campo de un bloque de plantilla: vinculado, enseña «Vinculado a …» con «Quitar» en vez de
 * su control; sin vincular, su control y debajo «Vincular a…» con lo que cabe en él. Si no cabe
 * nada (o el campo no es vinculable), el control tal cual.
 */
export const CampoVinculable = ({ campo, ruta, children, sinControl = false }: { campo: Campo; children: ReactNode; ruta: string; sinControl?: boolean }) => {
  const bloque = useBloqueDePlantilla()
  const { valores, cambiar, soloLectura } = useFormulario()
  if (!bloque) return <>{children}</>
  const enBloque = rutaEnElBloque(ruta, bloque.rutaDelBloque)
  if (enBloque === null) return <>{children}</>
  const rutaDelMapa = `${bloque.rutaDelBloque}.vinculos`
  const mapa = leer(valores, rutaDelMapa)
  const vinculo = mapa && typeof mapa === 'object' ? ((mapa as Record<string, Vinculo>)[enBloque] ?? null) : null
  const expresion = expresionDe(vinculo)
  const clase = claseDelCampo(bloque.datos?.bloques, bloque.bloque, enBloque)
  const todas = bloque.datos?.vinculables ?? []
  const caben = queCabenEn(todas, clase)

  const vincular = (nueva: string) => {
    const siguiente: Vinculo = vinculo && typeof vinculo === 'object' ? { ...vinculo, expresion: nueva } : nueva
    cambiar(rutaDelMapa, conVinculo(mapa, enBloque, siguiente))
    // En un campo de texto la expresión va también en el lugar del valor (§7.2): un campo obligatorio no se queda vacío.
    if (clase && CLASES_DE_TEXTO.has(clase) && !sinControl) cambiar(ruta, nueva)
  }
  const quitar = () => {
    cambiar(rutaDelMapa, conVinculo(mapa, enBloque, null))
    if (!sinControl && expresion && leer(valores, ruta) === expresion) cambiar(ruta, clase && CLASES_DE_TEXTO.has(clase) ? '' : undefined)
  }

  if (expresion) {
    return (
      <div className="flex flex-col gap-1.5">
        <p className="text-[13px] font-medium">{campo.etiqueta}</p>
        <div className="flex flex-wrap items-center gap-2 rounded-campo border border-violeta-borde bg-violeta-suave/40 px-3 py-2 text-[13px]">
          <Link2 aria-hidden className="size-4 shrink-0 text-violeta" />
          <span className="min-w-0 flex-1">
            Vinculado a <strong className="font-medium">{etiquetaDelVinculo(todas, expresion)}</strong>
            {todas.length > 0 && !todas.some((v) => v.expresion === expresion) && <span className="text-tenue"> · ese dato ya no existe en el tipo: al guardar no se aceptará</span>}
          </span>
          {!soloLectura && (
            <Boton icono={<X />} onClick={quitar} pequeno tipo="discreto">
              Quitar
            </Boton>
          )}
        </div>
      </div>
    )
  }

  // Sin nada que quepa, el campo es lo de siempre (y un destino sin campo no se enseña). Y un campo de
  // maquetación —una opción, una casilla, cuántas columnas o cuántos elementos— no se vincula: es cómo
  // se ve el bloque, no un dato del documento.
  if (soloLectura || caben.length === 0 || esDeMaquetacion(campo, clase)) return sinControl ? null : <>{children}</>

  const selector = (
    <label className="flex items-center gap-2 text-xs text-tenue">
      <Link2 aria-hidden className="size-3.5 shrink-0" />
      <span className="sr-only">Vincular «{campo.etiqueta}» a un dato del documento</span>
      <select
        className="h-7 max-w-full min-w-0 rounded-campo border border-linea bg-papel px-2 text-xs text-tinta-2 hover:border-linea-fuerte"
        onChange={(e) => {
          if (e.target.value) vincular(e.target.value)
        }}
        value=""
      >
        <option value="">Vincular a…</option>
        {agrupar(caben).map((g) => (
          <optgroup key={g.grupo} label={g.grupo}>
            {g.opciones.map((v) => (
              <option key={v.expresion} value={v.expresion}>
                {v.etiqueta}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </label>
  )

  if (sinControl) {
    return (
      <div className="flex flex-col gap-1.5">
        <p className="flex items-center gap-2 text-[13px] font-medium">
          {campo.etiqueta}
          <Pastilla clave="neutra">Solo vinculado</Pastilla>
        </p>
        {selector}
      </div>
    )
  }
  return (
    <div className="flex flex-col gap-1">
      {children}
      {selector}
    </div>
  )
}
